/* eslint-disable */ // @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import i18n from './ech-i18n';
import Uppy, { Meta, UploadResult, UppyFile } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import DragDrop from '@uppy/drag-drop';
import ProgressBar from '@uppy/progress-bar';
import German from '@uppy/locales/lib/de_DE';
import French from '@uppy/locales/lib/fr_FR';
import Italian from '@uppy/locales/lib/it_IT';

import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import '@uppy/progress-bar/dist/style.css';

const StyledUploadInput = styled.div`
  .uploadInputDragDrop button {
    font-size: ${rem(16)};
    border-radius: ${rem(3)};

    .uppy-DragDrop-inner {
      padding: ${rem(20)};
    }

    .uppy-DragDrop-label,
    .uppy-DragDrop-note {
      font-size: ${rem(16)};
    }
  }

  .uploadInputProgressBar {
    .uppy-ProgressBar {
      margin-top: ${rem(-3)};
    }
    .uppy-ProgressBar-inner {
      box-shadow: none;
    }
  }
`;

/**
 * The `UploadInput` component let's you upload content for the SmartCMS.
 */
const UploadInput = ({
  id,
  allowedFileTypes,
  maxNumberOfFiles,
  maxFileSize,
  onChange,
  disabled,
  value,
  api,
  formId,
  locale,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uppyRef = useRef<Uppy | null>(null);

  useEffect(() => {
    const uppy = new Uppy({
      id: 'upload',
      locale: locale === 'it' ? Italian : locale === 'fr' ? French : German,
      restrictions: {
        maxNumberOfFiles,
        maxFileSize, // 2 MB
        allowedFileTypes,
      },
      autoProceed: true,
      allowMultipleUploads: maxNumberOfFiles > 1,
    });

    // Save Uppy instance to useRef to persist across renders
    uppyRef.current = uppy;

    // Event Handlers
    const handleFileAdded = (file: UppyFile<Meta, Record<string, never>>) => {
      uppy.setFileMeta(file.id, { formId });
    };

    const handleUpload = () => {
      setUploadError(null);
    };

    const handleComplete = (
      result: UploadResult<Meta, Record<string, never>>,
    ) => {
      const files = [];
      if (result.successful?.length) {
        result.successful.forEach((singleResult) => {
          files.push({
            name: singleResult.name,
            fileId: singleResult?.response?.body?.result?.fileId,
          });
        });
        setUploadedFiles(files);
        onChange(files);
      } else {
        uppy.emit('info-visible');
      }
    };

    const handleInfoVisible = () => {
      const info = uppy.getState().info[0];
      const file = uppy.getFiles()[0];

      if (info?.type === 'error') {
        setUploadError(info.message);

        if (file) {
          uppy.removeFile(file.id);
          onChange(undefined);
        }
      }
    };

    // Setup Uppy plugins
    uppy.use(XHRUpload, {
      endpoint: api,
    });
    uppy.use(DragDrop, {
      target: `#uploadInputDragDrop${id}`,
      locale: {
        pluralize: (n) => n,
        strings: {
          dropHereOr: i18n.dragOr[locale],
          browse: i18n.browse[locale],
        },
      },
    });
    uppy.use(ProgressBar, {
      target: `#uploadInputProgressBar${id}`,
      hideAfterFinish: false,
    });

    // Register event listeners
    uppy.on('file-added', handleFileAdded);
    uppy.on('upload', handleUpload);
    uppy.on('complete', handleComplete);
    uppy.on('info-visible', handleInfoVisible);

    // Cleanup on component unmount
    return () => {
      uppy.off('file-added', handleFileAdded);
      uppy.off('upload', handleUpload);
      uppy.off('complete', handleComplete);
      uppy.off('info-visible', handleInfoVisible);
      uppy.destroy();
    };
  }, [
    allowedFileTypes,
    api,
    formId,
    id,
    locale,
    maxFileSize,
    maxNumberOfFiles,
    onChange,
  ]);

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      setUploadedFiles(value);
    }
  }, [value]);

  return (
    <>
      <StyledUploadInput
        style={{
          ...(uploadedFiles.length > 0 && { display: 'none' }),
          ...(disabled && { pointerEvents: 'none', opacity: '0.3' }),
        }}
      >
        <div id={`uploadInputDragDrop${id}`} className="uploadInputDragDrop" />
        <div
          id={`uploadInputProgressBar${id}`}
          className="uploadInputProgressBar"
        />
      </StyledUploadInput>
      {uploadedFiles.length > 0 ? (
        <div
          style={{
            border: '2px dashed #adadad',
            backgroundColor: '#fff',
            textAlign: 'center',
            padding: '24px',
          }}
        >
          <a
            href={`${api}?fileId=${uploadedFiles[0].fileId}`}
            target="_blank"
            style={{
              textDecoration: 'none',
            }}
            rel="noreferrer noopener"
          >
            {uploadedFiles[0].name}
          </a>
        </div>
      ) : null}
      {uploadError && (
        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
          {uploadError}
        </Form.Control.Feedback>
      )}
    </>
  );
};

UploadInput.propTypes = {
  /**
		The on change callback:
	*/
  onChange: PropTypes.func,
  allowedFileTypes: PropTypes.array,
  maxNumberOfFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  formId: PropTypes.string,
};

UploadInput.defaultProps = {
  onChange: () => {},
  allowedFileTypes: ['image/*', '.jpg', '.jpeg', '.png', '.gif'],
  maxNumberOfFiles: 1,
  maxFileSize: 1024 * 1024 * 1, // 2 MB
  disabled: false,
  value: [],
  formId: 'subsidies',
};

const Upload = ({
  id,
  label,
  value,
  onChange,
  error,
  isRequired,
  isDisabled,
  prefix,
  suffix,
  secondaryText,
  errorRenderer,
  api,
  formId,
  locale,
}) => {
  return (
    <Form.Group
      className="mb-3"
      controlId={id}
      style={{ position: 'relative' }}
    >
      {label ? (
        <Form.Label>
          {label}
          {isRequired ? ' *' : ''}
        </Form.Label>
      ) : null}
      {secondaryText ? (
        <Form.Text className="text-muted">{secondaryText}</Form.Text>
      ) : null}
      {prefix ? <span>{prefix}</span> : null}
      <UploadInput
        id={id.match(/\[(\d+)\]/)[1]}
        value={value}
        allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
        maxNumberOfFiles={1}
        maxFileSize={1024 * 1024 * 2}
        onChange={(url) => onChange(url)}
        disabled={isDisabled}
        api={api}
        formId={formId}
        locale={locale}
      />
      {suffix ? <span>{suffix}</span> : null}
      {error ? (
        errorRenderer ? (
          errorRenderer(error)
        ) : (
          <Form.Control.Feedback type="invalid">
            Please fill out this field!
          </Form.Control.Feedback>
        )
      ) : null}
    </Form.Group>
  );
};

export default Upload;
